import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import React, { useState, useEffect, createContext, Dispatch, SetStateAction } from 'react';

import { InterceptedFormsObject } from '../../@types';
import { USER_TOKEN_KEY, BASE_URL } from '../../configs/envs';

export interface AxiosInterceptorProps {
  children: React.ReactNode;
}

export interface FormInterceptorContextProps {
  interceptedFormsObject: InterceptedFormsObject | undefined;
  setInterceptedFormsObject: Dispatch<SetStateAction<InterceptedFormsObject | undefined>>;
  hasInterceptedForms?: boolean;
  setHasInterceptedForms: Dispatch<SetStateAction<boolean>>;
}

export const FormInterceptorContext = createContext<FormInterceptorContextProps>({
  interceptedFormsObject: undefined,
  setInterceptedFormsObject: {} as Dispatch<SetStateAction<InterceptedFormsObject | undefined>>,
  hasInterceptedForms: false,
  setHasInterceptedForms: {} as Dispatch<SetStateAction<boolean>>,
});

export const AxiosInterceptor: React.FC<AxiosInterceptorProps> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [interceptedFormsObject, setInterceptedFormsObject] = useState<InterceptedFormsObject>();
  const [hasInterceptedForms, setHasInterceptedForms] = useState<boolean>(false);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(USER_TOKEN_KEY);

        if (!config?.headers) {
          throw new Error("Expected 'config' and 'config.headers' not to be undefined");
        }

        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        config.headers['Time-Zone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Per-request metadata consumed by the BFF requestLogMiddleware to record a
        // user page-view row: `page-url` is the screen the request originated from
        // (required: the BFF skips logging without it) and `created-at` is the
        // client-side request timestamp (the BFF falls back to its own clock if absent).
        // Only origin + pathname is sent, so the query string and hash are stripped and
        // sensitive route data (e.g. an Auth0 `?code=`/`state=` callback, reset tokens,
        // invite links) never lands in the persisted page-view audit log.
        config.headers['page-url'] = `${window.location.origin}${window.location.pathname}`;
        config.headers['created-at'] = new Date().toISOString();

        const isFormData = config.data instanceof FormData;
        if (!isFormData) {
          config.headers['Content-Type'] = 'application/json';
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        if (response.data?.interactionFormData || response.data?.actionTriggers) {
          const interactionFormData = response.data?.interactionFormData ?? [];
          if (
            interactionFormData.length > 0 ||
            response.data?.actionTriggers?.length ||
            response.data?.isCheckin ||
            response.data?.multiOnSiteSuggestionsAvailable
          ) {
            // Raw BFF interaction-form payload has no precise FE type (its
            // `template*` field names differ from TemplateRowDataType); kept as
            // `any` to preserve the existing assignment to `forms: FormObject[]`.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const formList = interactionFormData.map((obj: any) => {
              return {
                ...obj.templateContent,
                ruleId: obj.id,
                isMandatory: obj.templateIsMandatory,
                isRepeatable: obj.templateIsRepeatable,
                templateDisplayLimit: obj.templateDisplayLimit,
                displayCount: obj.displayCount,
                isDownloadable: obj.templateIsDownloadable,
                isEditable: obj.templateIsEditable,
                partActionId: obj?.partActionId,
              };
            });
            const originalUrl = response.config?.url?.replace(BASE_URL, '');
            const originalHttpMethod = response.config?.method?.toUpperCase();
            // The request body is only a JSON string for JSON requests; it may be
            // absent or a FormData instance. Parse only strings, and never throw on
            // a non-JSON body, otherwise a successful response would be rejected.
            const rawRequestData = response.config?.data;
            let originalPayload: unknown = rawRequestData;
            if (typeof rawRequestData === 'string') {
              try {
                originalPayload = JSON.parse(rawRequestData);
              } catch {
                originalPayload = rawRequestData;
              }
            }
            const formsObject: InterceptedFormsObject = {
              url: originalUrl || '',
              method: originalHttpMethod || 'PUT',
              payload: originalPayload,
              forms: formList,
              actionTriggers: response.data?.actionTriggers,
              isCheckin: response.data?.isCheckin,
              multiOnSiteSuggestionsAvailable: response.data?.multiOnSiteSuggestionsAvailable,
            };

            setInterceptedFormsObject(formsObject);
          } else {
            setInterceptedFormsObject(undefined);
          }
          setHasInterceptedForms(true);
        }

        return response;
      },
      async (error) => {
        const config = error?.config;

        // Guard `config`: a non-request error (e.g. a cancellation) can reach here
        // with `error.config` undefined, so only attempt the retry when it exists.
        if (config && error?.response?.status === 401 && !config.sent) {
          config.sent = true;

          const token = await getAccessTokenSilently();

          if (token) {
            localStorage.setItem(USER_TOKEN_KEY, token);

            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${token}`,
            };
          }

          return axios(config);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
    // Mount-only: interceptors are registered once and ejected on unmount.
    // `getAccessTokenSilently` is intentionally omitted, because re-running this effect
    // on its identity change would register duplicate interceptors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormInterceptorContext.Provider
      value={{
        interceptedFormsObject,
        setInterceptedFormsObject,
        hasInterceptedForms,
        setHasInterceptedForms,
      }}
    >
      {children}
    </FormInterceptorContext.Provider>
  );
};
